import type { FeatureFlags } from '@/config/feature-flags';
import { findQuickLinkPage, listAvailableQuickLinkPages } from '@/config/quickLinkPages';
import { usePermissions } from '@/hooks/usePermissions';

interface HomepageSelectorProps {
  value: string | null;
  onChange: (next: string | null) => void;
  flags?: FeatureFlags | null;
}

export function HomepageSelector({ value, onChange, flags }: HomepageSelectorProps) {
  const { hasPermission } = usePermissions();
  const options = listAvailableQuickLinkPages(hasPermission, flags);
  const isUnknown = Boolean(value) && !findQuickLinkPage(value as string, hasPermission);

  return (
    <div className="space-y-2">
      <label htmlFor="homepage-path" className="block text-sm font-medium text-gray-800">
        Homepage
      </label>
      <p className="text-xs text-gray-500">
        Pick the page you want to land on when you sign in. Choose Default to keep the role-based
        home.
      </p>
      <select
        id="homepage-path"
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-gray-800 focus:ring-gray-800"
      >
        <option value="">Default (use my role&apos;s home)</option>
        {isUnknown && <option value={value as string}>(removed) {value}</option>}
        {options.map((page) => (
          <option key={page.key} value={page.pathTemplate}>
            {page.label}
          </option>
        ))}
      </select>
      {isUnknown && (
        <p className="text-xs text-amber-600">
          Saved homepage is no longer in the catalog. Pick a new one or reset to default.
        </p>
      )}
    </div>
  );
}
